/**
 * Returns a function that is triggered at most once during a given window.
 * @param {Function} fn Callback.
 * @param {number} [wait=300] Window in milliseconds.
 * @returns {Function}
 */
function throttle(fn, wait = 300) {
  let throttleTimeoutId = -1;
  let tick = false;

  return () => {
    clearTimeout(throttleTimeoutId);
    throttleTimeoutId = setTimeout(fn, wait);

    if (!tick) {
      fn.call();
      tick = true;
      setTimeout(() => { tick = false; }, wait);
    }
  };
}

if (!customElements.get('sticky-atc')) {
  customElements.whenDefined('product-form').then(() => {
    class StickyAtc extends customElements.get('product-form') {
      constructor() {
        super();

        this.section = this.closest('.section-main-product');
        this.mainProductForm = this.section.querySelector('.js-product');
        this.buyButtons = this.section.querySelector('buy-buttons');
        this.imageContainer = this.querySelector('.sticky-atc__image');
        this.variantContainer = this.querySelector('.sticky-atc__details__variant');
        this.variantTitle = this.querySelector('.sticky-atc__details__variant__title');
        this.variantTitle?.toggleAttribute('hidden', !this.section.querySelector('variant-picker'));

        this.isMonarchHybrid = this.dataset.monarchHybrid === 'true';
        this.currentVariantAvailable = this.dataset.currentVariantAvailable === 'true';
        this.variantChangeLink = this.querySelector('.sticky-atc__details__variant__change');
        this.monarchPrompt = this.querySelector('[data-monarch-prompt]');
        this.mainVariantIdInput = this.section.querySelector('buy-buttons input[name="id"]');
        this.mainAddButton = this.section.querySelector('buy-buttons [name="add"]');

        if (this.isMonarchHybrid) {
          this.initialiseMonarchPresentation();
          this.initMonarchPurchaseStateObserver();
        }

        if (this.isMonarchHybrid && this.variantChangeLink) {
          this.boundOnMonarchOptionsClick = this.onMonarchOptionsClick.bind(this);
          this.variantChangeLink.addEventListener('click', this.boundOnMonarchOptionsClick);
        }

        this.throttledOnScroll = throttle(StickyAtc.handleScroll.bind(this));
        window.addEventListener('scroll', this.throttledOnScroll);
        this.section.addEventListener('on:variant:change', this.onVariantChange.bind(this));

        if (!this.isMonarchHybrid) {
          this.section.addEventListener('on:media-gallery:change', this.updateImage.bind(this));
        }

        this.updateImage();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.throttledOnScroll);
        if (this.boundOnMonarchOptionsClick && this.variantChangeLink) {
          this.variantChangeLink.removeEventListener('click', this.boundOnMonarchOptionsClick);
        }
        if (this.monarchPurchaseObserver) this.monarchPurchaseObserver.disconnect();
      }

      formatMonarchVariantTitle(title) {
        return (title || '')
          .replace(/\s*·\s*\d+(?:\.\d+)?\s*g\b/gi, '')
          .replace(/\s*-\s*\d+(?:\.\d+)?\s*g\b/gi, '')
          .replace(/\bCroMo\s*4130\s*Rail\b/gi, 'CrMo')
          .replace(/\b4130\s*CrMo\s*Rail\b/gi, 'CrMo')
          .replace(/\bCrMo\s*4130\s*Rail\b/gi, 'CrMo')
          .replace(/\bTi-MX\s*Rail\b/gi, 'Ti-MX')
          .replace(/\s*\/\s*/g, ' · ')
          .replace(/\s{2,}/g, ' ')
          .trim();
      }

      initialiseMonarchPresentation() {
        if (this.variantTitle && this.variantTitle.textContent.trim()) {
          this.variantTitle.textContent = this.formatMonarchVariantTitle(this.variantTitle.textContent);
        }
        const currentVariantId = this.dataset.currentVariant || '';
        if (!currentVariantId) this.setMonarchPresentationState('empty');
        else if (this.currentVariantAvailable) this.setMonarchPresentationState('available');
        else this.setMonarchPresentationState('unavailable');
      }

      setMonarchPresentationState(state) {
        if (!this.isMonarchHybrid) return;
        this.dataset.monarchState = state;
        if (this.monarchPrompt) this.monarchPrompt.hidden = state !== 'empty';
      }

      isMainVariantPurchasable(expectedVariantId = '') {
        const variantId = this.mainVariantIdInput ? String(this.mainVariantIdInput.value || '') : '';
        if (!variantId) return false;
        if (expectedVariantId && String(expectedVariantId) !== variantId) return false;
        if (!this.mainAddButton) return this.currentVariantAvailable;
        return !this.mainAddButton.disabled && this.mainAddButton.getAttribute('aria-disabled') !== 'true';
      }

      syncMonarchPurchaseState(variant = null) {
        if (!this.isMonarchHybrid) return;
        const variantId = this.mainVariantIdInput ? String(this.mainVariantIdInput.value || '') : '';
        const purchasable = variantId ? this.isMainVariantPurchasable(variantId) : false;
        this.currentVariantAvailable = purchasable;
        this.dataset.currentVariantAvailable = purchasable ? 'true' : 'false';
        this.dataset.currentVariant = variantId;

        if (!variantId) this.setMonarchPresentationState('empty');
        else if (purchasable) this.setMonarchPresentationState('available');
        else this.setMonarchPresentationState('unavailable');

        this.updateAddToCartButton(variant || (variantId ? { id: variantId, available: purchasable } : null));
      }

      scheduleMonarchPurchaseSync(variant) {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => this.syncMonarchPurchaseState(variant));
        });
        window.setTimeout(() => this.syncMonarchPurchaseState(variant), 80);
      }

      initMonarchPurchaseStateObserver() {
        if (!this.isMonarchHybrid) return;
        if (this.mainAddButton && 'MutationObserver' in window) {
          this.monarchPurchaseObserver = new MutationObserver(() => this.syncMonarchPurchaseState());
          this.monarchPurchaseObserver.observe(this.mainAddButton, {
            attributes: true,
            attributeFilter: ['disabled', 'aria-disabled'],
            childList: true,
            subtree: true,
            characterData: true
          });
        }
        window.setTimeout(() => this.syncMonarchPurchaseState(), 0);
      }

      scrollToMonarchOptions() {
        const target = this.section.querySelector('.monarch-picker')
          || this.section.querySelector('variant-picker')
          || this.section.querySelector('#product-info');
        if (target && target.offsetParent) theme.scrollToRevealElement(target);
      }

      onMonarchOptionsClick(evt) {
        if (!this.isMonarchHybrid) return;
        evt.preventDefault();
        this.scrollToMonarchOptions();
      }

      async handleSubmit(evt) {
        evt.preventDefault();
        const mainVariantIdInput = this.section.querySelector('product-form buy-buttons input[name="id"]');
        const variantId = mainVariantIdInput ? mainVariantIdInput.value : '';

        if (this.isMonarchHybrid && (!variantId || !this.isMainVariantPurchasable(variantId))) {
          this.scrollToMonarchOptions();
          return;
        }

        if (!variantId) {
          const target = this.section.querySelector('#product-info');
          if (target && target.offsetParent) theme.scrollToRevealElement(target);
          return;
        }

        const customFormValid = this.mainProductForm.validate();
        if (!customFormValid || !this.mainProductForm.form.reportValidity()) {
          if (!customFormValid) {
            if (this.isMonarchHybrid) this.scrollToMonarchOptions();
            else theme.scrollToRevealElement(this.mainProductForm);
          } else {
            const input = Array.from(this.mainProductForm.form.elements).find((el) => !el.checkValidity());
            setTimeout(() => theme.scrollToRevealElement(input), 100);
          }
          return;
        }

        this.form.querySelectorAll('[data-copied]').forEach((el) => el.remove());
        const formData = new FormData(this.mainProductForm.form);
        for (const p of formData) {
          if (!this.form.querySelector(`[name="${p[0]}"]`)) {
            const input = document.createElement('input');
            input.name = p[0];
            input.value = p[1];
            input.hidden = true;
            input.setAttribute('data-copied', '');
            this.form.append(input);
          }
        }

        super.handleSubmit(evt);
      }

      static handleScroll() {
        const header = document.querySelector('.section-header');
        const topOffset = document.querySelector('.pageheader--sticky') && header ? header.offsetHeight : 0;
        if (this.buyButtons) {
          this.classList.toggle('sticky-atc--out', this.buyButtons.getBoundingClientRect().bottom > topOffset);
        }
        document.body.classList.toggle(
          'scrolled-to-bottom',
          window.scrollY + window.innerHeight + 100 > document.body.scrollHeight
        );
      }

      onVariantChange(evt) {
        const idInput = this.form?.querySelector('[name="id"]');
        const variant = evt.detail.variant;

        if (variant) {
          if (this.variantTitle) {
            this.variantTitle.textContent = this.isMonarchHybrid
              ? this.formatMonarchVariantTitle(variant.title)
              : variant.title;
          }
          if (this.variantContainer) this.variantContainer.hidden = false;
          if (idInput) idInput.value = variant.id;
        } else {
          if (this.variantContainer) this.variantContainer.hidden = true;
          if (this.variantTitle) this.variantTitle.textContent = '';
          if (idInput) idInput.value = '';
        }

        if (this.isMonarchHybrid) {
          this.dataset.currentVariant = variant ? String(variant.id) : '';
          if (!variant) {
            this.currentVariantAvailable = false;
            this.dataset.currentVariantAvailable = 'false';
            this.setMonarchPresentationState('empty');
          }
          this.scheduleMonarchPurchaseSync(variant);
        }

        this.updateImage(variant);
        if (!this.isMonarchHybrid) this.updateAddToCartButton(variant);
        this.setErrorMsgState();
      }

      getMonarchVariantImage(variant) {
        const fallback = this.dataset.monarchDefaultImage || '';
        if (!variant || !Array.isArray(variant.options)) return fallback;
        const railIndex = parseInt(this.dataset.monarchRailIndex, 10);
        const colourIndex = parseInt(this.dataset.monarchColourIndex, 10);
        if (Number.isNaN(railIndex) || Number.isNaN(colourIndex) || railIndex < 0 || colourIndex < 0) return fallback;

        const rail = String(variant.options[railIndex] || '').toLowerCase();
        const colour = String(variant.options[colourIndex] || '').toLowerCase();
        let railKey = '';
        let colourKey = '';
        if (rail.includes('ti-mx')) railKey = 'timx';
        else if (rail.includes('cromo') || rail.includes('crmo')) railKey = 'crmo';

        if (colour.includes('turquoise') || colour === 'blue') colourKey = 'blue';
        else if (colour.includes('black')) colourKey = 'black';
        else if (colour.includes('red')) colourKey = 'red';
        else if (colour.includes('mango') || colour.includes('orange')) colourKey = 'orange';
        else if (colour.includes('slate') || colour.includes('grey') || colour.includes('gray')) colourKey = 'grey';

        if (!railKey || !colourKey) return fallback;
        const imageMap = {
          crmo: {
            blue: this.dataset.monarchImageCrmoBlue,
            black: this.dataset.monarchImageCrmoBlack,
            red: this.dataset.monarchImageCrmoRed,
            orange: this.dataset.monarchImageCrmoOrange,
            grey: this.dataset.monarchImageCrmoGrey
          },
          timx: {
            blue: this.dataset.monarchImageTimxBlue,
            black: this.dataset.monarchImageTimxBlack,
            red: this.dataset.monarchImageTimxRed,
            orange: this.dataset.monarchImageTimxOrange,
            grey: this.dataset.monarchImageTimxGrey
          }
        };
        return imageMap[railKey]?.[colourKey] || fallback;
      }

      setMonarchImage(url) {
        if (!this.imageContainer || !url) return;
        let image = this.imageContainer.querySelector('[data-monarch-sticky-image]');
        if (!image) {
          image = document.createElement('img');
          image.alt = '';
          image.width = 600;
          image.height = 450;
          image.loading = 'eager';
          image.decoding = 'async';
          image.setAttribute('data-monarch-sticky-image', '');
          this.imageContainer.replaceChildren(image);
        }
        if (image.getAttribute('src') !== url) image.setAttribute('src', url);
      }

      updateImage(variant = null) {
        if (this.isMonarchHybrid) {
          if (variant) this.setMonarchImage(this.getMonarchVariantImage(variant));
          else if (!this.dataset.currentVariant) this.setMonarchImage(this.dataset.monarchDefaultImage || '');
          return;
        }
        const mainImage = this.section.querySelector('.media-gallery .main-image .slider__item.is-active .product-media img');
        if (mainImage) this.imageContainer.innerHTML = mainImage.outerHTML;
        else this.imageContainer.textContent = '';
      }

      updateAddToCartButton(variant) {
        if (!this.submitBtn) return;
        if (this.isMonarchHybrid) {
          this.submitBtn.removeAttribute('aria-disabled');
          this.submitBtn.disabled = false;
          const variantId = this.mainVariantIdInput ? String(this.mainVariantIdInput.value || '') : '';
          if (!variantId) {
            this.submitBtn.textContent = this.submitBtn.dataset.chooseOptionsText || 'SELECT OPTIONS';
            return;
          }
          if (!this.isMainVariantPurchasable(variantId)) {
            this.submitBtn.textContent = this.submitBtn.dataset.viewOptionsText || 'SELECT OPTIONS';
            return;
          }
          this.submitBtn.textContent = this.submitBtn.dataset.addToCartText;
          return;
        }

        const variantAvailable = variant && variant.available;
        const unavailableStr = variant ? theme.strings.noStock : this.submitBtn.dataset.unavailableText;
        this.submitBtn.textContent = variantAvailable ? this.submitBtn.dataset.addToCartText : unavailableStr;
      }
    }

    customElements.define('sticky-atc', StickyAtc);
  });
}
